/**
 * Teri — the floating AI assistant.
 *
 * Launcher (7118:199814 light / 7118:199832 dark): the EXACT blop exports
 * from the design — Teri peeking, no waving; white circle in light, dark
 * circle in dark. Hover/focus slides out the purple pill (7118:201278 /
 * 9187:187807).
 *
 * Chat: a companion window that stays open across navigation until closed —
 * the conversation itself lives in AssistantContext (app level), so route
 * changes never reset it. It docks two ways, the member's choice, remembered
 * across sessions:
 *   • floating — a window in the lower-right corner (product rule
 *     2026-07-21); no backdrop, the platform stays fully usable behind it.
 *   • sidebar  — a full-height rail on the right that SQUEEZES the page
 *     (body padding, see index.css) so nothing is hidden behind it.
 * On phones both collapse to the same bottom sheet. Dismissing keeps the
 * history — which is why the button reads "minimize", not "close" — and the
 * header's ＋ starts fresh.
 *
 * This file is the shell only: header, transcript and composer each live in
 * their own component beside it.
 */
import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { ArrowUpTrayIcon } from '@heroicons/react/24/outline';
import { EASE_OUT, EASE_DRAWER, DURATION_FAST, DURATION_NORMAL } from '@/lib/motion';
import { useAuth } from '@/hooks/useAuth';
import { useLocation } from 'react-router-dom';
import { useAssistant, useTeriGreeting } from '@/contexts/AssistantContext';
import AIDisabledNotice from './AIDisabledNotice';
import AnsweredNotice from './AnsweredNotice';
import AssistantComposer from './AssistantComposer';
import AssistantHeader from './AssistantHeader';
import AssistantMessageList from './AssistantMessageList';

const TERI_PEEK = '/assistant/teri-peek.png';
const TERI_BIG = '/tery/tery_techy.png';

// ── Onboarding tips (audit #14) ──────────────────────────────────────────
// KILL-SWITCH: set VITE_TERI_TIPS=off to remove the whole feature — the
// product owner's undo guarantee. Per-user dismissal below is the second.
const TIPS_ENABLED = (import.meta.env.VITE_TERI_TIPS ?? 'on') !== 'off';

// How long the self-introduction stays out, and how many landings it may use
// before giving up on someone who never opens Teri.
const INTRO_MS = 5000;
const INTRO_MAX_SHOWS = 3;

const TIPS_BY_PATH: [prefix: string, question: string][] = [
  ['/dashboard', 'Where do I start?'],
  ['/courses/', 'How do I invite learners to a course?'],
  ['/courses', 'How do I create my first course?'],
  ['/programs', 'How do learning programs work?'],
  ['/analytics', 'What do these numbers mean?'],
  ['/gamification', 'What happens when the leaderboard resets?'],
  ['/learn/prizes', 'How do I earn more points?'],
  ['/learn/', 'What should I do next?'],
];

function tipFor(pathname: string): string | null {
  const hit = TIPS_BY_PATH.find(([prefix]) => pathname.startsWith(prefix));
  return hit ? hit[1] : null;
}

const AssistantLauncher: React.FC = () => {
  const { user } = useAuth();
  const {
    open,
    setOpen,
    dock,
    messages,
    send,
    attachFiles,
    loadingConversation,
    answeredNotice,
  } = useAssistant();
  const greeting = useTeriGreeting();
  const [engaged, setEngaged] = useState(false);
  const { pathname } = useLocation();
  const tipsKey = `tereiko-teri-tips-dismissed:${user?.id ?? 'anon'}`;
  const [tipsDismissed, setTipsDismissed] = useState(
    () => localStorage.getItem(tipsKey) === '1'
  );
  // A finished answer outranks a page tip, and they share the same row beside
  // the blop — two pills there is clutter that can also overflow a phone.
  const tip =
    TIPS_ENABLED && !tipsDismissed && !open && !answeredNotice ? tipFor(pathname) : null;
  const railed = dock === 'sidebar';

  const dismissTips = () => {
    localStorage.setItem(tipsKey, '1');
    setTipsDismissed(true);
  };

  // Dismissal must be undoable (audit #14's own philosophy) — one stray ✕
  // otherwise hides the feature forever for that account.
  const restoreTips = () => {
    localStorage.removeItem(tipsKey);
    setTipsDismissed(false);
  };

  const askTip = (question: string) => {
    setOpen(true);
    void send(question);
  };
  const reduceMotion = useReducedMotion();

  // ── First-landing introduction ───────────────────────────────────────────
  // The pill only ever appeared on hover, so a new member saw an unexplained
  // blop in the corner and had no reason to touch it (Aleix, 2026-10-12).
  // It now slides out by itself to say what it is.
  //
  // It keeps doing that on each landing until the member has actually OPENED
  // Teri once — the first version showed it a single time and marked itself
  // seen immediately, so one unwatched page load spent the only chance and
  // nobody ever learned what the blop was (Aleix hit exactly that). Capped at
  // INTRO_MAX_SHOWS so it can never nag someone who simply is not interested.
  //
  // Kept separate from `engaged` so the timer cannot cancel a hover that is
  // still happening.
  const introCountKey = `tereiko-teri-intro-shown:${user?.id ?? 'anon'}`;
  const introDoneKey = `tereiko-teri-intro-done:${user?.id ?? 'anon'}`;
  const [intro, setIntro] = useState(false);

  useEffect(() => {
    if (!user?.id) return; // wait for the session, or 'anon' spends the shows

    let shows = 0;
    try {
      if (localStorage.getItem(introDoneKey) === '1') return;
      shows = Number(localStorage.getItem(introCountKey) ?? 0) || 0;
    } catch {
      shows = 0; // storage blocked: introduce Teri rather than stay silent
    }
    if (shows >= INTRO_MAX_SHOWS) return;

    setIntro(true);
    try {
      localStorage.setItem(introCountKey, String(shows + 1));
    } catch {
      /* it will simply introduce itself again next time */
    }
    const timer = setTimeout(() => setIntro(false), INTRO_MS);
    return () => clearTimeout(timer);
  }, [introCountKey, introDoneKey, user?.id]);

  // Opening Teri is the proof the introduction worked — stop offering it.
  const openAndRetireIntro = () => {
    setIntro(false);
    try {
      localStorage.setItem(introDoneKey, '1');
    } catch {
      /* the show cap still bounds it */
    }
    setOpen(!open);
  };
  const [bottomOffset, setBottomOffset] = useState(24);

  // Drop files anywhere on the panel — the panel owns these bounds, so the
  // whole surface is the target rather than just the paperclip.
  const [dragging, setDragging] = useState(false);
  const dragDepth = useRef(0);
  const carriesFiles = (e: React.DragEvent) =>
    Array.from(e.dataTransfer?.types ?? []).includes('Files');

  // Footer-aware placement: rest at the corner, but once the footer scrolls
  // into view glide up so the gap to it stays at half the footer's height.
  useEffect(() => {
    let raf = 0;
    const measure = () => {
      raf = 0;
      const footer = document.querySelector('footer');
      if (!footer) return;
      const rect = footer.getBoundingClientRect();
      const overlap = window.innerHeight - rect.top;
      const gap = rect.height / 2;
      setBottomOffset(Math.max(24, Math.round(overlap + gap)));
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(measure);
    };
    measure();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  // Geometry per dock. Below `lg` the rail would swallow the viewport, so it
  // falls back to the floating dock's bottom sheet.
  const panelGeometry = railed
    ? 'inset-y-0 right-0 w-full lg:w-[var(--teri-rail-w,380px)] rounded-none border-l ' +
      'max-lg:inset-y-auto max-lg:bottom-2 max-lg:left-2 max-lg:right-2 max-lg:w-auto ' +
      'max-lg:h-[min(560px,85vh)] max-lg:rounded-3xl max-lg:border'
    : 'right-4 bottom-28 w-[min(460px,calc(100vw-2rem))] h-[min(620px,calc(100vh-9rem))] rounded-3xl border ' +
      'max-sm:right-2 max-sm:left-2 max-sm:bottom-2 max-sm:w-auto max-sm:h-[min(560px,85vh)]';

  const panelSurface = railed
    ? 'bg-white/90 dark:bg-grey-950/90 border-border-secondary lg:shadow-[-10px_0_30px_rgba(0,0,0,0.07)]'
    : 'bg-white/85 dark:bg-grey-950/85 border-white/40 dark:border-white/10 shadow-2xl';

  // The rail slides in from its own edge; the corner window scales up in
  // place. Reduced motion keeps the fade and drops the travel.
  const panelMotion = reduceMotion
    ? { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 } }
    : railed
      ? {
          initial: { transform: 'translateX(100%)' },
          animate: { transform: 'translateX(0%)' },
          exit: { transform: 'translateX(100%)' },
        }
      : {
          initial: { opacity: 0, transform: 'translateY(16px) scale(0.98)' },
          animate: { opacity: 1, transform: 'translateY(0px) scale(1)' },
          exit: { opacity: 0, transform: 'translateY(16px) scale(0.98)' },
        };

  return (
    <>
      {/* ── Chat panel — corner window or right-hand rail ── */}
      <AnimatePresence initial={false}>
        {open && (
          <motion.section
            // Keyed by dock so switching plays a real exit/enter handoff
            // instead of snapping the geometry mid-flight.
            key={dock}
            {...panelMotion}
            transition={{
              duration: railed ? 0.26 : DURATION_NORMAL,
              ease: railed ? EASE_DRAWER : EASE_OUT,
            }}
            style={{ willChange: 'transform, opacity' }}
            role="dialog"
            aria-label="Chat with Teri"
            className={`fixed z-50 overflow-hidden flex flex-col backdrop-blur-2xl ${panelGeometry} ${panelSurface}`}
            data-testid="assistant-panel"
            data-dock={dock}
            onDragEnter={(e) => {
              if (!carriesFiles(e)) return;
              e.preventDefault();
              dragDepth.current += 1;
              setDragging(true);
            }}
            onDragOver={(e) => {
              if (!carriesFiles(e)) return;
              e.preventDefault();
            }}
            onDragLeave={() => {
              dragDepth.current = Math.max(0, dragDepth.current - 1);
              if (dragDepth.current === 0) setDragging(false);
            }}
            onDrop={(e) => {
              if (!carriesFiles(e)) return;
              e.preventDefault();
              dragDepth.current = 0;
              setDragging(false);
              void attachFiles(Array.from(e.dataTransfer.files));
            }}
          >
            <AssistantHeader railed={railed} onDismiss={() => setOpen(false)} />

            {loadingConversation ? (
              <div
                className="flex-1 flex items-center justify-center"
                data-testid="teri-loading-conversation"
              >
                <span className="w-6 h-6 rounded-full border-2 border-primary-400 border-t-transparent animate-spin" />
                <span className="sr-only">Loading this chat…</span>
              </div>
            ) : messages.length === 0 ? (
              /* ── Welcome state: Teri + prompt. The greeting is chrome, never
                    a message — it must not be persisted into a transcript or
                    replayed to the provider. ── */
              <div className="flex-1 flex flex-col items-center justify-center gap-4 px-6">
                <img
                  src={TERI_BIG}
                  alt=""
                  draggable={false}
                  className="w-[150px] select-none pointer-events-none"
                />
                <h2 className="text-lg font-semibold text-text-primary text-center">
                  How Can I Help You?
                </h2>
                <p className="text-xs text-text-tertiary text-center max-w-[260px]">
                  {greeting} Ask me anything — I&apos;ll stay right here while you move
                  around the platform.
                </p>
              </div>
            ) : (
              <AssistantMessageList />
            )}

            <AnsweredNotice variant="banner" />

            {/* Said before the first question, not only in reply to it — and
                with the switch itself, rather than directions to it. */}
            <AIDisabledNotice onNavigate={() => setOpen(false)} />

            <AssistantComposer />

            {TIPS_ENABLED && tipsDismissed && (
              <button
                type="button"
                onClick={restoreTips}
                data-testid="teri-tips-restore"
                className="self-center -mt-1 mb-1.5 text-[11px] text-text-tertiary hover:text-text-secondary underline underline-offset-2 transition-colors duration-fast"
              >
                💡 Show page tips again
              </button>
            )}

            <AnimatePresence>
              {dragging && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: DURATION_FAST, ease: EASE_OUT }}
                  className="absolute inset-2 z-10 rounded-2xl border-2 border-dashed border-primary-500 bg-white/80 dark:bg-grey-950/80 backdrop-blur-sm flex flex-col items-center justify-center gap-2 pointer-events-none"
                  data-testid="teri-drop-overlay"
                >
                  <ArrowUpTrayIcon className="w-6 h-6 text-primary-500" />
                  <p className="text-sm font-semibold text-text-primary">
                    Drop it here and I&apos;ll read it
                  </p>
                  <p className="text-[11px] text-text-tertiary">
                    PDF, Word, text, CSV or an image
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.section>
        )}
      </AnimatePresence>

      {/* ── Floating blop + hover pill ──
          Hidden while the rail is out: the blop is anchored to the same right
          edge and would sit on top of the panel it opens. */}
      {!(railed && open) && (
        <div
          className="fixed right-6 z-40 flex items-center gap-3 transition-[bottom] duration-fast ease-out-strong"
          style={{ bottom: bottomOffset }}
          data-testid="assistant-launcher"
        >
          {/* Only when the panel is shut — with it open, the banner inside it
              already carries this, and two copies is noise. */}
          {!open && <AnsweredNotice variant="pill" />}

          {tip && (
            <div className="flex items-center gap-1.5 max-w-[300px]" data-testid="teri-tip">
              <button
                type="button"
                onClick={() => askTip(tip)}
                className="px-3.5 py-2 rounded-full bg-surface-elevated border border-primary-500/30 text-[13px] font-semibold text-text-primary hover:border-primary-500/60 active:scale-[0.97] transition-[border-color,transform] duration-150 text-left"
              >
                💡 {tip}
              </button>
              <button
                type="button"
                onClick={dismissTips}
                aria-label="Don't show tips again"
                title="Don't show tips again"
                className="w-6 h-6 shrink-0 rounded-full flex items-center justify-center text-text-tertiary hover:text-text-secondary hover:bg-text-tertiary/10 text-xs transition-colors duration-150"
              >
                ✕
              </button>
            </div>
          )}
          <AnimatePresence>
            {(engaged || intro) && !open && (
              <motion.span
                initial={reduceMotion ? { opacity: 0 } : { opacity: 0, x: 8, scale: 0.96 }}
                animate={{ opacity: 1, x: 0, scale: 1 }}
                exit={reduceMotion ? { opacity: 0 } : { opacity: 0, x: 8, scale: 0.96 }}
                transition={{ duration: DURATION_NORMAL, ease: EASE_OUT }}
                className="px-5 py-2.5 rounded-full bg-primary-500 text-sm font-bold whitespace-nowrap select-none shadow-[inset_0_-2px_4px_rgba(0,0,0,0.18),0_4px_12px_rgb(var(--color-primary) / 0.35)]"
              >
                Teri - your AI assistant
              </motion.span>
            )}
          </AnimatePresence>

          <button
            type="button"
            aria-label="Teri - your AI assistant"
            aria-expanded={open}
            onClick={openAndRetireIntro}
            onMouseEnter={() => setEngaged(true)}
            onMouseLeave={() => setEngaged(false)}
            onFocus={() => setEngaged(true)}
            onBlur={() => setEngaged(false)}
            className="group focus-ring rounded-full active:scale-[0.95] transition-transform duration-fast ease-out-strong hover:scale-105"
          >
            {/* Design blop: theme circle + the design's transparent peek art
                (light: white circle; dark: dark circle — per the two frames). */}
            <span className="relative block w-20 h-20 rounded-full overflow-hidden shadow-[7px_12px_14px_rgba(0,0,0,0.18)] bg-background ring-1 ring-black/[0.06] dark:ring-white/10">
              <img
                src={TERI_PEEK}
                alt=""
                draggable={false}
                className="absolute bottom-[-10%] left-1/2 -translate-x-1/2 w-[96%] max-w-none select-none pointer-events-none"
              />
            </span>
          </button>
        </div>
      )}
    </>
  );
};

export default AssistantLauncher;
